/**
 * UserInitProvider
 *
 * Handles user initialization after authentication.
 * Calls GET /api/portal/users/me to ensure the user profile
 * is created (JIT provisioning) and loaded into context.
 */

import { createContext, useContext, useEffect, useState, useRef, type ReactNode } from 'react';
import { useAuth } from '../features/authentication';
import { Spinner, makeStyles, tokens } from '@fluentui/react-components';
import { useTenant } from '../contexts/TenantContext';
import { getTenantIdentifier } from '../services/tenant/tenantAccessor';

interface UserProfile {
  id: string;
  displayName: string;
  email: string;
  roles: string[];
  tenantId?: string;
  isNewUser?: boolean;
}

interface UserInitContextValue {
  isReady: boolean;
  isNewUser: boolean;
  profile: UserProfile | null;
  isLoading: boolean;
  error: Error | null;
}

const UserInitContext = createContext<UserInitContextValue | null>(null);

const useStyles = makeStyles({
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100vh',
    backgroundColor: tokens.colorNeutralBackground1,
  },
});

interface UserInitProviderProps {
  children: ReactNode;
}

/**
 * Provider that initializes user session after authentication.
 * Calls the /users/me endpoint to ensure user exists in the backend
 * and loads their profile into context.
 */
export function UserInitProvider({ children }: UserInitProviderProps) {
  const { isAuthenticated, isLoading: authLoading, getAccessToken } = useAuth();
  const { isResolved } = useTenant();
  const styles = useStyles();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [isReady, setIsReady] = useState(false);
  const initAttempted = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || authLoading || !isResolved || initAttempted.current) {
      return;
    }

    initAttempted.current = true;
    setIsLoading(true);

    const initUser = async () => {
      try {
        const token = await getAccessToken();
        if (!token) {
          setIsReady(true);
          setIsLoading(false);
          return;
        }

        const apiBase = import.meta.env.VITE_API_URL || 'https://localhost:7001/api';
        const tenantId = getTenantIdentifier();
        const headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        };
        if (tenantId) {
          headers['X-Tenant-Id'] = tenantId;
        }
        const response = await fetch(`${apiBase}/identity/users/me`, {
          headers,
        });

        if (response.ok) {
          const data = await response.json();
          setProfile(data);
        } else {
          console.warn('[UserInit] Failed to load user profile:', response.status);
        }
      } catch (err) {
        const initError = err instanceof Error ? err : new Error(String(err));
        console.warn('[UserInit] User init failed, continuing anyway:', initError.message);
        setError(initError);
      } finally {
        setIsReady(true);
        setIsLoading(false);
      }
    };

    initUser();
  }, [isAuthenticated, authLoading, isResolved, getAccessToken]);

  // Reset when user logs out
  useEffect(() => {
    if (!isAuthenticated) {
      initAttempted.current = false;
      setProfile(null);
      setIsReady(false);
      setError(null);
    }
  }, [isAuthenticated]);

  // Don't block on auth loading
  if (authLoading) {
    return null;
  }

  // If not authenticated, render children (ProtectedRoute handles redirect)
  if (!isAuthenticated) {
    return <>{children}</>;
  }

  // Brief loading while initializing
  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <Spinner size="large" label="Setting up your session..." />
      </div>
    );
  }

  const contextValue: UserInitContextValue = {
    isReady: isReady,
    isNewUser: profile?.isNewUser ?? false,
    profile,
    isLoading,
    error,
  };

  return (
    <UserInitContext.Provider value={contextValue}>
      {children}
    </UserInitContext.Provider>
  );
}

/**
 * Hook to access user initialization state.
 */
export function useUserInit(): UserInitContextValue {
  const context = useContext(UserInitContext);
  if (!context) {
    throw new Error('useUserInit must be used within UserInitProvider');
  }
  return context;
}
